// Iter 265 — Settings → Dashboard. One window drives the dashboard banner, the
// In/Out badges and the HR sidebar, plus the switch that gates staff who
// haven't filled their dates in.
import { useEffect, useState } from "react";
import { Cake, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

const KEYS = ["celebrations_enabled", "celebration_days_ahead",
              "celebrations_include_anniversaries", "celebrations_require_dates"];

export function CelebrationsCard({ canManage = true }) {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [missing, setMissing] = useState(null);

  useEffect(() => {
    api.get("/settings")
      .then((r) => setForm(Object.fromEntries(KEYS.map((k) => [k, r.data?.[k]]))))
      .catch(() => setForm(Object.fromEntries(KEYS.map((k) => [k, k === "celebration_days_ahead" ? 7 : true]))));
    api.get("/humi/celebrations-month")
      .then((r) => setMissing(r.data?.missing_count ?? null))
      .catch(() => setMissing(null));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/settings", {
        ...form,
        celebration_days_ahead: Math.max(0, Math.min(60, parseInt(form.celebration_days_ahead, 10) || 0)),
      });
      toast.success("Celebration settings saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save");
    } finally { setBusy(false); }
  };

  if (!form) return <div className="flex h-24 items-center justify-center"><Loader2 className="h-4 w-4 animate-spin text-slate-300" /></div>;

  const row = (key, title, hint) => (
    <div className="flex items-start justify-between gap-4 border-t border-slate-100 py-3 first:border-t-0 dark:border-slate-800">
      <div className="min-w-0">
        <div className="text-sm font-medium text-slate-800 dark:text-slate-100">{title}</div>
        <p className="text-xs text-slate-500 dark:text-slate-400">{hint}</p>
      </div>
      <Switch checked={!!form[key]} disabled={!canManage} data-testid={`celebrations-${key}`}
        onCheckedChange={(v) => set(key, v)} />
    </div>
  );

  return (
    <div data-testid="settings-celebrations-card">
      <div className="mb-3 flex items-center gap-2">
        <Cake className="h-4 w-4 text-primary" />
        <h3 className="font-heading text-base font-bold text-slate-900 dark:text-slate-100">Celebrations</h3>
      </div>

      {row("celebrations_enabled", "Show celebrations",
        "The dashboard banner, the In/Out board badges and the HR sidebar. Off hides all three.")}

      <div className="flex items-start justify-between gap-4 border-t border-slate-100 py-3 dark:border-slate-800">
        <div className="min-w-0">
          <div className="text-sm font-medium text-slate-800 dark:text-slate-100">Days ahead</div>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            How early a birthday or anniversary appears, and how long it stays — it clears the day
            after. Weekend and statutory-holiday birthdays shift to the next business day.
          </p>
        </div>
        <Input type="number" min={0} max={60} value={form.celebration_days_ahead ?? 7}
          disabled={!canManage} data-testid="celebrations-days-ahead" className="w-20 shrink-0"
          onChange={(e) => set("celebration_days_ahead", e.target.value)} />
      </div>

      {row("celebrations_include_anniversaries", "Include work anniversaries",
        "Off shows birthdays only; start dates are still collected.")}

      {row("celebrations_require_dates", "Require staff to fill their dates",
        "Staff missing a birthday or start date are held on a sign-in prompt until both are saved. "
        + "Nobody is exempt — super admins included. Partners and customers are never asked.")}

      {missing !== null && (
        <p className="mt-2 text-xs text-amber-600" data-testid="celebrations-missing-count">
          {missing === 0
            ? "Every active staff member has both dates on file."
            : `${missing} staff member${missing === 1 ? "" : "s"} still ${missing === 1 ? "has" : "have"} a date missing`
              + `${form.celebrations_require_dates ? " — they'll be prompted on their next sign-in." : "."}`}
        </p>
      )}

      {canManage && (
        <div className="mt-4 flex justify-end">
          <Button onClick={save} disabled={busy} data-testid="celebrations-save" className="gap-1.5">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save
          </Button>
        </div>
      )}
    </div>
  );
}
